import { VersionSource, VersionAction, Root, ExpandedSurvey } from "@aidkitorg/types/lib/survey";
import { diffJson, type Diff } from "@aidkitorg/types/lib/utils/jsonDiff";
import { surveyNodeLabel } from "@aidkitorg/types/lib/utils/surveyNodeLabel";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { get_deployment, usePost } from "./API";
import React from "react";
import { Bars3Icon } from '@heroicons/react/24/solid'
import { SpacedSpinner, useInterval } from "./Util";
import { Checkbox, Spinner, useToast } from "@aidkitorg/component-library";
import { MIGRATIONS } from "./Migrations";
import { ChangeSet, Location, Move, Patch } from "@aidkitorg/typesheets/lib/collab";
import { useCollabActions } from "./CollabWrapper";
import { useAsyncEffect } from "./Hooks/AsyncEffect";
import { PublicConfigurationContext } from "./Context";
import { BranchDropdown } from "./Config/BranchDropdown";
import { snakeCaseToTitleCase } from "@aidkitorg/types/lib/utils/snakeCaseToTitleCase";

const Distro = React.lazy(() => import("@aidkitorg/typesheets/lib/distroeditor"));

function formatLocation(loc: Location | undefined): string {
  if (!loc) return '(removed)';
  if (loc.kind === 'object') return `${loc.id} object[${loc.prop}]`;
  return `${loc.id} array[${loc.sortKey}]`;
}

type CollabDiffCellData = {
  patch?: { value: string; author: string };
  move?: { loc: string; author: string };
};

type CollabDiffRow = {
  objectId: string;
  prev: CollabDiffCellData | null;
  next: CollabDiffCellData | null;
};

function buildCollabDiffRows(added: ChangeSet, removed: ChangeSet): CollabDiffRow[] {
  const prevMap = new Map<string, CollabDiffCellData>();
  const nextMap = new Map<string, CollabDiffCellData>();

  const processEntry = (map: Map<string, CollabDiffCellData>, id: string, op: Move | Patch) => {
    const existing = map.get(id) ?? {};
    if (op.op === 'patch') {
      map.set(id, { ...existing, patch: { value: String(op.value ?? ''), author: op.author?.name ?? '' } });
    } else {
      map.set(id, { ...existing, move: { loc: formatLocation(op.loc), author: op.author?.name ?? '' } });
    }
  };

  for (const [id, , op] of removed) {
    if (op) processEntry(prevMap, id, op);
  }
  for (const [id, , op] of added) {
    if (op) processEntry(nextMap, id, op);
  }

  const allIds = new Set([...Array.from(prevMap.keys()), ...Array.from(nextMap.keys())]);
  return Array.from(allIds).map(objectId => ({
    objectId,
    prev: prevMap.get(objectId) ?? null,
    next: nextMap.get(objectId) ?? null,
  }));
}

function CollabDiffCell({ data }: { data: CollabDiffCellData | null }) {
  if (!data || (!data.patch && !data.move)) return <span className="text-gray-400">N/A</span>;
  return <div className="space-y-1">
    {data.patch && <div>
      <span className="font-mono">{data.patch.value || <em className="text-gray-400">empty</em>}</span>
      {data.patch.author && <span className="text-gray-400 text-xs ml-1">({data.patch.author})</span>}
    </div>}
    {data.move && <div className="text-blue-600">
      Location: <span className="font-mono ">{data.move.loc}</span>
      {data.move.author && <span className="text-gray-400 text-xs ml-1">({data.move.author})</span>}
    </div>}
  </div>;
}

async function fetchOpsEvents(url: string): Promise<ChangeSet> {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Failed to fetch ops file: ${response.status}`);
  const parsed = await response.json();
  return Array.isArray(parsed) ? parsed : [];
}

const DIFF_V2_PAGE_SIZE = 500;

function DiffV2Table({ diffs, loading }: { diffs: Diff[]; loading: boolean }) {
  const [showAll, setShowAll] = useState(false);

  if (loading) return <div className="p-8"><SpacedSpinner /></div>;
  if (diffs.length === 0) return <div className="p-4 text-sm text-gray-500">No changes</div>;

  const truncated = !showAll && diffs.length > DIFF_V2_PAGE_SIZE;
  const displayDiffs = truncated ? diffs.slice(0, DIFF_V2_PAGE_SIZE) : diffs;

  return (
    <div className="mt-2">
      <div className="text-xs text-gray-500 px-2 pb-1">
        {diffs.length} difference{diffs.length !== 1 ? 's' : ''}
      </div>
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className="bg-gray-100 text-left">
            <th className="p-2 border border-gray-300 font-semibold">Path</th>
            <th className="p-2 border border-gray-300 font-semibold">Type</th>
            <th className="p-2 border border-gray-300 font-semibold bg-red-50">Old value</th>
            <th className="p-2 border border-gray-300 font-semibold bg-green-50">New value</th>
          </tr>
        </thead>
        <tbody>
          {displayDiffs.map((diff, i) => (
            <tr key={i} className="border-b hover:bg-gray-50">
              <td className="p-2 border border-gray-200 font-mono align-top">
                {diff.path}
                {diff.breadcrumbs && diff.breadcrumbs.length > 0 && (
                  <div className="text-gray-400 font-sans mt-0.5">{diff.breadcrumbs.join(' › ')}</div>
                )}
              </td>
              <td className={`p-2 border border-gray-200 align-top font-medium ${
                diff.type === 'added' ? 'text-green-700' :
                  diff.type === 'removed' ? 'text-red-700' :
                    'text-amber-700'
              }`}>
                {diff.type}
              </td>
              <td className="p-2 border border-gray-200 bg-red-50 align-top font-mono">
                {diff.oldValue !== undefined ? JSON.stringify(diff.oldValue) : ''}
              </td>
              <td className="p-2 border border-gray-200 bg-green-50 align-top font-mono">
                {diff.newValue !== undefined ? JSON.stringify(diff.newValue) : ''}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {truncated && (
        <button
          className="text-xs text-blue-600 hover:text-blue-800 p-2 border-0 bg-transparent cursor-pointer"
          onClick={() => setShowAll(true)}
        >
          … {diffs.length - DIFF_V2_PAGE_SIZE} more rows — click to show all
        </button>
      )}
    </div>
  );
}

type VersionInfo = {
  versionId: string;
  lastModified: string;
  isLatest: boolean | undefined;
  lastModifiedUser?: string | undefined;
  commitMessage?: string | undefined;
  versionSource?: VersionSource;
  distroReviewId?: string | undefined;
  distroCommitId?: string | undefined;
  parentOpsVersion?: string | undefined;
  action?: VersionAction;
};

function colorsForAction(action: VersionAction) {
  switch (action) {
    case 'publish':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'auto_save':
      return 'bg-lime-100 text-lime-800 border-lime-200';
    case 'sync':
    case 'sync_with_conflicts':
      return 'bg-amber-100 text-amber-800 border-amber-200';
    case 'enable':
      return 'bg-indigo-100 text-indigo-800 border-indigo-200';
    case 'create_branch':
    case 'snapshot':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'bulk_write':
      return 'bg-purple-100 text-purple-800 border-purple-200';
    case 'reset':
      return 'bg-violet-100 text-violet-800 border-violet-200';
    case 'unknown':
      return 'bg-gray-100 text-gray-800 border-gray-200';
    case 'restore':
      return 'bg-orange-100 text-orange-800 border-orange-200';
    default:
      return '';
  }
}

export default function ConfigVersionsPage(props: {
  displayCollabVersion?: boolean
}) {
  const [aILoading, setAILoading] = useState(false);
  const [fetchedDate, setFetchedDate] = useState('');
  const [fetchedVersion, setFetchedVersion] = useState<ExpandedSurvey | null>(null);
  const [fetchedVersionEvents, setFetchedVersionEvents] = useState<ChangeSet | null>(null);
  const [name, setName] = useState(window.location.hash.slice(1));
  const [showDiffToCurrent, setShowDiffToCurrent] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);
  const [survey, setSurvey] = useState<ExpandedSurvey>({ survey: [], notifications: [], personas: [], config: { version: 'v1', name: get_deployment() } });
  const [versions, setVersions] = useState<VersionInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [restoreVersionLoading, setRestoreVersionLoading] = useState(false);
  const [showCollabDiff, setShowCollabDiff] = useState(false);
  const [collabDiffLoading, setCollabDiffLoading] = useState(false);
  const [collabDiffToPrev, setCollabDiffToPrev] = useState<ChangeSet | null>(null);
  const [collabDiffFromPrev, setCollabDiffFromPrev] = useState<ChangeSet | null>(null);
  const [hideCollabAutoSaves, setHideCollabAutoSaves] = useState(false);
  const selectedVersionRef = useRef<VersionInfo | null>(null);

  // v2 diff: client-side diffing via diffJson
  const [showDiffV2, setShowDiffV2] = useState(false);
  const [fetchedVersionStr, setFetchedVersionStr] = useState<string | null>(null);
  const [prevFetchedVersionStr, setPrevFetchedVersionStr] = useState<{ versionId: string; json: string } | null>(null);
  const [v2Diffs, setV2Diffs] = useState<Diff[]>([]);
  const [v2DiffLoading, setV2DiffLoading] = useState(false);
  const distroRef = useRef<React.ComponentRef<typeof Distro>>(null);

  const doMagic = usePost('/program/admin/magicV2');
  const loadSurvey = usePost('/survey/load_survey');
  const retrieveSurveyVersion = usePost('/survey/retrieve_survey_version');
  const retrieveCollabVersion = usePost('/survey/retrieve_collab_version');
  const saveSurvey = usePost('/survey/save_survey', { compressRequestPayload: true });
  const listSurveyVersions = usePost('/survey/list_survey_versions');
  const listCollabVersions = usePost('/survey/list_collab_versions');
  const restoreCollabToPublishedVersion = usePost('/survey/restore_collab_to_published_version');
  const [versionsTruncated, setVersionsTruncated] = useState(true);
  const loadingVersionsRef = useRef(false);
  const loadMoreVersionsRef = useRef(null);
  const publicConfiguration = useContext(PublicConfigurationContext);

  const { toast } = useToast();

  const initialBranchParam = useRef(Number(new URLSearchParams(window.location.search).get('branch')) || undefined);

  const {
    // if collab is not enables, this hook doesn't do much
    collabEnabled,
    collabReady,
    branchId,
    branchName,
    branches,
    switchBranch,
    getCollabState,
    restoreBranchVersion,
  } = useCollabActions({
    surveyName: name,
    tabId: '',
    mergeReceivedEventsCallback: () => {},
    autosave: false,
    initialBranchId: initialBranchParam.current,
    source: 'distro',
  });


  // versionIdMarker and keyMarker are for s3 pagination
  const versionIdMarker = useRef('');
  const keyMarker = useRef('');

  const loadVersions = useCallback(async ({append = false}: {append?: boolean} = {}) => {
    if (!name) return;
    if (props.displayCollabVersion && !branchId) return;

    const listFn = props.displayCollabVersion ? listCollabVersions : listSurveyVersions;
    loadingVersionsRef.current = true;
    const versionsResult = await listFn({
      name: name,
      branchId: branchId!,
      pageSize: 50,
      versionIdMarker: versionIdMarker.current,
      keyMarker: keyMarker.current,
    }) as {
      list: VersionInfo[];
      keyMarker: string;
      versionIdMarker: string;
      isTruncated: boolean;
    }
    if (versionsResult) {
      keyMarker.current = versionsResult.keyMarker;
      versionIdMarker.current = versionsResult.versionIdMarker;
      setVersionsTruncated(versionsResult.isTruncated)
      if (append) {
        // append is used for paginating additional results
        setVersions(prev => [...prev, ...versionsResult.list]);
      } else {
        setVersions(versionsResult.list);
        onSelectVersion(versionsResult.list[0]);
      }
    }

    loadingVersionsRef.current = false;
  }, [name, props.displayCollabVersion, branchId]);

  useEffect(() => {
    load(name);
    loadVersions()
  }, [name, loadVersions]);

  useEffect(() => {
    // This gets triggered when the element with ref={loadMoreVersionsRef} is in view
    const observer = new IntersectionObserver((entries) => {
      const first = entries[0];
      if (first.isIntersecting && keyMarker.current && versionIdMarker.current && !loadingVersionsRef.current) {
        loadVersions({append: true}).then(() => {
          // Reobserve the element after loading is complete in case it's still in view
          if (!loadingVersionsRef.current && loadMoreVersionsRef.current) {
            observer.observe(loadMoreVersionsRef.current);
          }
        });
      }
    }, {});
    if (loadMoreVersionsRef.current) {
      observer.observe(loadMoreVersionsRef.current);
    }
    return () => {
      // On cleanup, disconnect observer
      observer.disconnect();
    };
  }, [loadMoreVersionsRef, loadingVersionsRef.current, loadVersions]);

  useEffect(() => {
    if (distroRef.current) {
      distroRef.current?.initialize(fetchedVersion as Root);
    }
  }, [fetchedVersion]);

  async function load(_n?: string) {
    let n = _n || name;
    if (!n) {
      let _n = prompt('Enter a name to load');
      if (_n) {
        n = _n;
        setName(_n);
      }
    }
    if (!n) {
      return;
    }
    window.location.hash = '#' + n;
    const surveyResp = await loadSurvey({ name: n })
    if (!surveyResp) {
      alert('Error loading survey');
      return;
    }
    setSurvey(surveyResp.config);

    await getCollabState();
  }

  async function onSelectVersion(versionInfo: VersionInfo) {    
    selectedVersionRef.current = versionInfo;
    setLoading(true);
    setFetchedDate(versionInfo.lastModified);

    let version;
    if (props.displayCollabVersion) {
      if (!branchId) return;
      const res = await retrieveCollabVersion({ name: name, branchId, versionId: versionInfo.versionId });
      version = res.survey;
      if (selectedVersionRef.current?.versionId === versionInfo.versionId) {
        setFetchedVersionEvents(null);
      }
      // Events feed only the collab diff, so they load without holding up the version view.
      if (res.eventsUrl) {
        fetchOpsEvents(res.eventsUrl).then(events => {
          if (selectedVersionRef.current?.versionId === versionInfo.versionId) setFetchedVersionEvents(events);
        }).catch(e => {
          console.error('Error fetching collab version events', e);
          toast({ description: 'Could not load the events for this version', variant: 'error' });
        });
      }
    } else {
      version = await retrieveSurveyVersion({ name: name, versionId: versionInfo.versionId });
    }

    if (version) {
      setFetchedVersion(JSON.parse(version) as ExpandedSurvey);
      setFetchedVersionStr(version);
      setPrevFetchedVersionStr(null);
    }
    setLoading(false);
  }

  useAsyncEffect(async (signal) => {
    if (!showCollabDiff || !fetchedVersionEvents || !props.displayCollabVersion || !branchId) return;

    const selectedId = selectedVersionRef.current?.versionId;
    const versionIndex = versions.findIndex(v => v.versionId === selectedId);
    const prevVersionIndex = versionIndex !== -1 && versionIndex < versions.length - 1 ? versionIndex + 1 : -1;

    setCollabDiffLoading(true);

    try {
      if (prevVersionIndex !== -1) {
        const prevVersionId = versions[prevVersionIndex].versionId;
        const prevRes = await retrieveCollabVersion({ name, branchId, versionId: prevVersionId });
        if (signal.aborted) return;
        const prevEvents = prevRes?.eventsUrl ? await fetchOpsEvents(prevRes.eventsUrl) : [];
        if (signal.aborted) return;
        const selectedOpIds = new Set(fetchedVersionEvents.filter(e => e[2]).map(e => e[2].id));
        const prevOpIds = new Set(prevEvents.filter(e => e[2]).map(e => e[2].id));
        const addedEvents = fetchedVersionEvents.filter(e => e[2] && !prevOpIds.has(e[2].id));
        const removedEvents = prevEvents.filter(e => e[2] && !selectedOpIds.has(e[2].id));
        setCollabDiffToPrev(addedEvents);
        setCollabDiffFromPrev(removedEvents);
      } else {
        setCollabDiffToPrev(fetchedVersionEvents);
        setCollabDiffFromPrev([]);
      }
    } catch (e) {
      if (signal.aborted) return;
      console.error('Error loading collab diff', e);
      toast({ description: 'Could not load the collab diff for this version', variant: 'error' });
      setCollabDiffToPrev([]);
      setCollabDiffFromPrev([]);
    } finally {
      if (!signal.aborted) {
        setCollabDiffLoading(false);
      }
    }
  }, [showCollabDiff, fetchedVersionEvents, versions, name, branchId, props.displayCollabVersion]);


  useAsyncEffect(async (signal) => {
    if (!showDiffV2 || !fetchedVersionStr) {
      setV2Diffs([]);
      return;
    }

    setV2DiffLoading(true);
    let oldJson: Root = JSON.parse(fetchedVersionStr);

    // This is only needed for a rare case in which the old JSON is an array (v1 format)
    // and so converting it to ExpandedSurvey form should improve the diff results
    if (Array.isArray(oldJson)) {
      oldJson = { survey: oldJson, personas: [] } satisfies ExpandedSurvey;
    }

    if (showDiffToCurrent) {
      setV2Diffs(diffJson(oldJson, survey, { labelForNode: surveyNodeLabel }));
      setV2DiffLoading(false);
      return;
    }

    const versionIndex = versions.findIndex((v) => v.versionId === selectedVersionRef.current?.versionId);
    const prevVersionInfo =
      versionIndex !== -1 && versionIndex < versions.length - 1 ? versions[versionIndex + 1] : null;

    if (!prevVersionInfo) {
      setV2Diffs([]);
      setV2DiffLoading(false);
      return;
    }

    let prevJson: string | null = null;
    if (prevFetchedVersionStr?.versionId === prevVersionInfo.versionId) {
      prevJson = prevFetchedVersionStr.json;
    } else {
      if (props.displayCollabVersion && branchId) {
        const prevRes = await retrieveCollabVersion({ name, branchId, versionId: prevVersionInfo.versionId });
        if (signal.aborted) return;
        prevJson = prevRes?.survey ?? null;
      } else {
        prevJson = await retrieveSurveyVersion({ name, versionId: prevVersionInfo.versionId });
        if (signal.aborted) return;
      }
      if (prevJson) setPrevFetchedVersionStr({ versionId: prevVersionInfo.versionId, json: prevJson });
    }

    setV2Diffs(prevJson ? diffJson(JSON.parse(prevJson), oldJson, { labelForNode: surveyNodeLabel }) : []);
    setV2DiffLoading(false);
  }, [showDiffV2, fetchedVersionStr, showDiffToCurrent, survey, versions, name, branchId, props.displayCollabVersion]);

  (window as any).magic = async (data: any) => {
    return await doMagic(data);
  }

  async function AISummary() {
    const prompt_eng = `Write a concise summary of the changes that were made to this json, which represents a survey configuration.
            The changes are given as a list of differences, each with a path, a type (added, removed, or changed), and the old and new values.
            Since the intended audience isn't familiar with json, please use plain english and refer to the json as a "survey configuration".
            If there were no changes say "No Changes".
            When you're done say STOP ${JSON.stringify(v2Diffs)}`

    setAILoading(true);
    const response = await (window as any).magic({
      model: "gpt-4o",
      message: prompt_eng,
      max_tokens: 1000,
      top_p: 1,
      frequency_penalty: 0,
      presence_penalty: 0,
      stop: "STOP",
      temperature: 0,
    });
    if (response.choices?.[0]?.message?.content) {
      alert(response.choices[0]?.message?.content);
    } else {
      console.log(response);
      alert('Uh oh, we had trouble with your request. Try again!')
    }
    setAILoading(false);
  }

  const restoreVersion = useCallback(async () => {
    if (props.displayCollabVersion) {
      if (!selectedVersionRef.current?.versionId) {
        alert('Could not find version id for selected version');
        return;
      }

      const confirmRestore = confirm(
        'Are you sure you want to restore this auto-saved version? \
                \n \nThis will NOT change the published survey, but it will clear any in-progress changes not included in this version. \
                \n \nBe sure to coordinate this action with your team, and refresh your distro page before resuming work!!')

      if (confirmRestore) {
        if (!branchId) {
          toast({ description: 'Could not find branch id for selected version', variant: 'error' });
          return;
        }

        setRestoreVersionLoading(true);
        try {
          // set s3 keys to empty so we refetch first page of versions after reload
          keyMarker.current = '';
          versionIdMarker.current = '';

          await restoreBranchVersion(selectedVersionRef.current.versionId);
          // On success restoreBranchVersion reloads the page and notifies other tabs via enable_collab.
        } finally {
          setRestoreVersionLoading(false);
        }
      }
    } else if (collabEnabled) {
      if (!selectedVersionRef.current?.versionId) {
        alert('Could not find version id for selected version');
        return;
      }

      const confirmRestore = confirm(
        'Are you sure you want to restore this version? \
        \n \nThis will overwrite the current configuration for this program with the version selected here \
        \n \nBe sure to coordinate this action with your team!!');

      if (!confirmRestore) {
        return;
      }

      setRestoreVersionLoading(true);
      // set s3 keys to empty so we refetch first page of versions
      keyMarker.current = '';
      versionIdMarker.current = '';

      const response = await restoreCollabToPublishedVersion({
        surveyName: name,
        versionId: selectedVersionRef.current.versionId,
      });

      if (!('error' in response)) {
        if (response.success) {
          alert('Successfully restored version');
        } else {
          alert(`Failed to restore version: ${response.reason}`);
        }
      }

      load(name);
      loadVersions();
      setRestoreVersionLoading(false);
    } else {
      const commitMessage = prompt(
        'Are you sure you want to restore this version? \
                \n \nThis will overwrite the current configuration for this program with the version selected here \
                \n \nBe sure to coordinate this action with your team!! \
                \n \nPlease enter a commit message for this version:')
        
      // If the user cancels the prompt, don't restore the version.
      if (commitMessage === null) { 
        return;
      }

      setRestoreVersionLoading(true);
      try {
        // set s3 keys to empty so we refetch first page of versions
        keyMarker.current = '';
        versionIdMarker.current = '';

        alert(JSON.stringify(await saveSurvey({
          name: name,
          content: JSON.stringify(fetchedVersion),
          commitMessage,
          source: 'distro',
          action: 'restore'
        })));

        load(name);
        loadVersions();
      } finally {
        setRestoreVersionLoading(false);
      }
    }
  }, [
    props.displayCollabVersion,
    branchId,
    restoreBranchVersion,
    collabEnabled,
    fetchedVersion,
    fetchedVersionEvents,
    loadVersions,
    toast,
    name,
    load,
    restoreCollabToPublishedVersion,
    saveSurvey,
  ]);

  const dateOptions: Intl.DateTimeFormatOptions = {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: true
  };
  const formatDate = (date: string) => {
    const dateFormatter: Intl.DateTimeFormat = new Intl.DateTimeFormat('en-US', dateOptions);
    try {
      return dateFormatter.format(new Date(date));
    }
    catch (e) {
      return '';
    }
  }

  const deploymentName = useMemo(() => get_deployment(), []);

  const restoreButtonLabel = props.displayCollabVersion
    ? `Restore branch "${branchName}" to this version`
    : 'Restore published to this version';

  return <>
    <div className={"flex h-12 border-b border-blue-300 z-[40] relative top items-center " + (process.env.NODE_ENV === 'development' ? 'bg-green-200' : 'bg-blue-200')}>
      <div className="font-bold text-2xl text-blue-400 ml-3">
        [{deploymentName}] {props.displayCollabVersion ? 'Auto-Saved' : 'Published'} Distro History: <span className="border-0 bg-transparent ml-2 mr-1 text-blue-600 font-normal">{name}</span>
      </div>
      {props.displayCollabVersion && collabEnabled && branches && branches.length > 0 && (
        <div className="ml-1">
          <BranchDropdown
            branches={branches}
            currentBranchId={branchId}
            onSwitchBranch={(id) => {
              keyMarker.current = '';
              versionIdMarker.current = '';
              setFetchedVersionEvents(null);
              switchBranch(id);
            }}
          />
        </div>
      )}
      <a href={'/config#' + name} className="border-0 bg-transparent p-2 text-blue-600 hover:text-blue-800 ml-auto">
        Back to Configuration
      </a>
    </div>
    {props.displayCollabVersion && collabReady && !collabEnabled && (
      <div className="flex items-center gap-2 bg-yellow-50 border-b border-yellow-300 px-4 py-2 text-sm text-yellow-800">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 shrink-0" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
        </svg>
        Collab is currently disabled for this program.
      </div>
    )}
    {name && <div className="flex" style={{ height: 'calc(100vh - 6.5rem)' }}>
      <div className="overflow-y-auto w-full">
        <div className="sticky top-0 bg-white px-4 py-0 z-[40] border-b shadow-sm">
          <div className="flex items-center">
            <div>
              <div className="text-lg pt-4">{formatDate(fetchedDate)}</div>
              <div className="d-block text-xs">Below is a JSON log of the changes saved {showDiffToCurrent ? 'since the selected version' : 'in the selected version'}</div>
            </div>
            {!showSidebar &&
                            <button 
                              title="Open Sidebar"
                              onClick={() => setShowSidebar(!showSidebar)} 
                              className="border-0 bg-transparent h-2 w-2 text-blue-600 hover:text-blue-800 ml-auto pr-3 mb-6">
                              <Bars3Icon className="h-7 w-7 mr-6"/>
                            </button>}
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="show-diff-v2"
              className="form-checkbox h-3 w-3 cursor-pointer"
              checked={showDiffV2}
              onChange={() => setShowDiffV2(!showDiffV2)}
            />
            <label htmlFor="show-diff-v2" className="ml-1 text-sm mb-0 cursor-pointer">
              Load JSON diff
            </label>
            {props.displayCollabVersion && <>
              <span className="text-gray-500 pb-1 text-lg mr-2 ml-2">|</span>
              <input
                type="checkbox"
                id="show-collab-diff"
                className="form-checkbox h-3 w-3 cursor-pointer"
                checked={showCollabDiff}
                onChange={() => setShowCollabDiff(!showCollabDiff)}
              />
              <label htmlFor="show-collab-diff" className="ml-1 text-sm mb-0 cursor-pointer">
                Load collab diff
              </label>
            </>}
            <span className="text-gray-500 pb-1 text-lg mr-2 ml-2">|</span>
            <input
              type="checkbox"
              id="show-diff"
              className="form-checkbox h-3 w-3 cursor-pointer"
              checked={showDiffToCurrent}
              onChange={() => setShowDiffToCurrent(!showDiffToCurrent)}
            />
            <label htmlFor="show-diff" className="ml-1 text-sm mb-0 cursor-pointer">
              Compare to current {props.displayCollabVersion ? 'published ' : ''}config
            </label>

            <button
              onClick={AISummary}
              disabled={!showDiffV2 || v2DiffLoading}
              title={showDiffV2 ? undefined : 'Load the JSON diff first'}
              className="border-0 bg-transparent p-2 text-blue-600 hover:text-blue-800 ml-auto text-sm disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {!aILoading ? 'Ask AI to summarize changes' : <SpacedSpinner />}
            </button>
            <span className="text-gray-500 pb-1 text-lg">|</span>
            <button
              onClick={restoreVersion}
              disabled={restoreVersionLoading}
              className="border-0 bg-transparent p-2 text-blue-600 hover:text-blue-800 text-sm disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {restoreVersionLoading ? <Spinner size="small" className="inline-flex !w-auto p-0" /> : restoreButtonLabel}
            </button>
          </div>
        </div>
        <div>
          <div className="p-3">
            {showDiffV2 && <DiffV2Table diffs={v2Diffs} loading={v2DiffLoading} />}
            {showCollabDiff && props.displayCollabVersion && <div className="mt-2">
              {collabDiffLoading || !fetchedVersionEvents ? <div className="p-8"><SpacedSpinner /></div> : (() => {
                const rows = buildCollabDiffRows(collabDiffToPrev ?? [], collabDiffFromPrev ?? []);
                if (rows.length === 0) return <div className="p-4 text-sm text-gray-500">No collab changes</div>;
                const truncated = rows.length > 100;
                const displayRows = truncated ? rows.slice(0, 100) : rows;
                return <>
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr className="bg-gray-100 text-left">
                        <th className="p-2 border border-gray-300 font-semibold">Object ID</th>
                        <th className="p-2 border border-gray-300 font-semibold bg-red-50">Previous</th>
                        <th className="p-2 border border-gray-300 font-semibold bg-green-50">New</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayRows.map(row => (
                        <tr key={row.objectId} className="border-b hover:bg-gray-50">
                          <td className="p-2 border border-gray-200 font-mono text-gray-400">{row.objectId.slice(0, 8)}…</td>
                          <td className="p-2 border border-gray-200 bg-red-50"><CollabDiffCell data={row.prev} /></td>
                          <td className="p-2 border border-gray-200 bg-green-50"><CollabDiffCell data={row.next} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {truncated && <div className="text-xs text-gray-400 p-2">… {rows.length - 100} more rows not shown</div>}
                </>;
              })()}
            </div>}
          </div>
        </div>
        {!loading && <Distro ref={distroRef} types='src/survey.ts' name='Root' migrations={MIGRATIONS} programConfig={publicConfiguration} readonly />}
      </div>
      {showSidebar && <div className="sidebar overflow-y-auto w-96 border">
        <div className="sticky top-0 bg-white p-4 border-b shadow-sm">
          <button
            title="Close Sidebar"
            onClick={() => setShowSidebar(!showSidebar)} 
            className="text-lg border-0 bg-transparent">
            Saved Versions
          </button>
          {props.displayCollabVersion && (
            <Checkbox
              label="Hide AutoSaves"
              onChange={() => setHideCollabAutoSaves(!hideCollabAutoSaves)}
              checked={hideCollabAutoSaves}
            />
          )}
        </div>
        <ul className="flex-grow-1 space-y-2">
          {(hideCollabAutoSaves ? (versions || []).filter(v => v.action !== 'auto_save') : (versions || [])).map((s) => {
            const lastModified = formatDate(s.lastModified);
            return <li
              key={s.lastModified}
              onClick={() => onSelectVersion(s)}
              className={`my-0 py-3 px-4 cursor-pointer text-sm border border-solid border-gray-300 border-t-1 border-x-0 border-b-0 hover:bg-blue-50 ${selectedVersionRef.current?.versionId === s.versionId ? 'bg-blue-100' : ''}`}
            >
              <span className="text-gray-600">{lastModified}</span>

              {(s.action || s.versionSource || s.lastModifiedUser) && (
                <div className="flex items-center gap-1">
                  {s.lastModifiedUser && (<div className="text-gray-500 text-xs">{s.lastModifiedUser}</div>)}
                  {s.action && (
                    <span className={`text-xs font-medium rounded px-1.5 py-0.5 border ${colorsForAction(s.action)}`}>
                      {snakeCaseToTitleCase(s.action)}
                    </span>
                  )}
                  {s.versionSource && s.versionSource !== 'unknown' && (
                    <span className="text-xs font-medium rounded px-1.5 py-0.5 bg-gray-100 text-gray-700 border border-gray-200">
                      {snakeCaseToTitleCase(s.versionSource)}
                    </span>
                  )}
                </div>
              )}

              {s.commitMessage && (
                <div className={`mt-1 font-medium text-gray-700 p-1.5 ${formatDate(fetchedDate) === lastModified ? 'bg-blue-200' : 'bg-gray-100'}`}>
                  {s.commitMessage}
                </div>
              )}

              {s.distroReviewId && <a href={`/distro-review/${s.distroReviewId}`} className="text-gray-500 text-xs">Distro Review #{s.distroReviewId}</a>}
            </li>
          })}
          <li ref={loadMoreVersionsRef} className="flex justify-center items-center">
            { versionsTruncated && <SpacedSpinner /> }
          </li>
        </ul>
      </div>}
    </div>}
  </>
};
